import { useQuery } from '@apollo/client/react';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { useHostNavigate } from '../../../shared/ui';
import { RECENT_QUOTES_QUERY } from '../graphql/queries';
import { lastTouched, type RecentQuotesQueryData } from '../types';
import { Panel, Stat } from './panel';

function relative(value: string | null): string {
  if (!value) return 'not priced yet';
  return formatDistanceToNow(parseISO(value), { addSuffix: true });
}

/**
 * Pick up where you left off.
 *
 * A distributor's quotations are the only thing on this portal that is
 * genuinely theirs, and every visit that isn't a brand new enquiry starts by
 * finding one of them. Before this, finding one meant landing on a welcome
 * message, clicking through to the quote screen and paging a grid of cards.
 */
export function RecentQuotesPanel({
  distributorId,
  distributorLoading,
}: {
  distributorId: number | null;
  /** True while the company lookup this panel is scoped by is still in flight. */
  distributorLoading: boolean;
}) {
  const hostNavigate = useHostNavigate();

  const { data, loading, error } = useQuery<RecentQuotesQueryData>(RECENT_QUOTES_QUERY, {
    variables: { distributorId, limit: 6 },
    skip: !distributorId,
    fetchPolicy: 'cache-and-network',
  });

  const quotes = data?.quotes ?? [];
  const total = data?.quoteSummary?.count ?? 0;
  const mostRecent = quotes.length ? lastTouched(quotes[0]) : null;

  return (
    <Panel
      title="Your quotations"
      subtitle={total ? `${total} in total` : undefined}
      action={{ label: 'All quotations', to: '/quote' }}
      loading={distributorLoading || (loading && quotes.length === 0)}
      error={error}
      empty={
        // Without a company there is nothing to scope the list by. Say so —
        // an earlier version left the panel spinning, which reads as a slow
        // network rather than a lookup that already failed.
        !distributorLoading && !distributorId
          ? 'Your quotations could not be loaded because we could not identify your company.'
          : !loading && quotes.length === 0
            ? 'Nothing quoted yet. Start one above.'
            : undefined
      }
    >
      <div className="flex gap-6 pb-3 mb-1 border-b border-hilevel-border">
        <Stat value={total} label="Quotations" />
        <Stat value={mostRecent ? relative(mostRecent) : '—'} label="Last touched" />
      </div>

      <div className="divide-y divide-hilevel-border">
        {quotes.map((quote) => {
          const touched = lastTouched(quote);
          return (
            <button
              key={quote.id}
              type="button"
              onClick={() => hostNavigate(`/quote/${quote.uuid}`)}
              title={
                quote.createdAt
                  ? `Created ${format(parseISO(quote.createdAt), 'd/M/yyyy, HH:mm')}`
                  : undefined
              }
              className="flex items-center gap-2 py-1.5 w-full text-left bg-transparent border-0 p-0 cursor-pointer min-w-0 group"
            >
              <span className="flex-1 min-w-0">
                <span className="block text-sm text-hilevel-slate truncate group-hover:text-hilevel-red transition-colors">
                  {quote.description?.trim() || 'Untitled quotation'}
                </span>
                <span className="block text-[11px] text-hilevel-gray">
                  <span className="font-mono">Q{quote.id}</span>
                  {quote.type ? ` · ${quote.type}` : ''}
                </span>
              </span>
              <span className="shrink-0 text-[10px] text-hilevel-gray">{relative(touched)}</span>
            </button>
          );
        })}
      </div>
    </Panel>
  );
}
